/**
 * Milestones permission predicates.
 *
 * Mirrors the visibility rules from MILESTONES-IMPLEMENTATION-PLAN.md §2.3 on
 * top of the existing `user.can(...)` API (which already applies the
 * admin/account-owner/locked-permission behavior). There is deliberately no
 * unconditional `isAdmin` shortcut here — locked permission records must win.
 */

export const MILESTONE_PERMISSIONS = {
    view: 'View Milestones',
    create: 'Create Milestones',
    edit: 'Edit Milestones',
    delete: 'Delete Milestones',
};

export const VIEW_CONTRIBUTIONS = 'View Contributions';

const ALL_MANAGE = [MILESTONE_PERMISSIONS.view, MILESTONE_PERMISSIONS.create, MILESTONE_PERMISSIONS.edit, MILESTONE_PERMISSIONS.delete];

const can = (user, permission) => user?.can?.(permission) === true;

/**
 * Can the viewer see another person's badges (View Milestones gate)?
 * Owners always read their own badges — including donation badges — without
 * View Milestones or View Contributions.
 * @param {object} user - loggedInUser (with .can/.canAny bound by assignPermissions).
 * @param {string} personID - profile subject.
 */
export const canViewPersonMilestones = (user, personID) =>
    !!personID && (user?.id === personID || can(user, MILESTONE_PERMISSIONS.view));

/**
 * Do donation-based badges/history require the financial gate for this
 * viewer/subject pair? Owners are exempt; everyone else needs View
 * Contributions in addition to View Milestones.
 */
export const needsFinancialGate = (user, personID) =>
    user?.id !== personID && !can(user, VIEW_CONTRIBUTIONS);

/**
 * Can the viewer open the organization Milestones management page?
 * Any of the four Milestones permissions (admins satisfy unlocked
 * permissions through the normal can() path).
 */
export const canViewMilestoneManagement = (user) =>
    user?.canAny?.(ALL_MANAGE) === true;

export const canCreateMilestones = (user) => can(user, MILESTONE_PERMISSIONS.create);
export const canEditMilestones = (user) => can(user, MILESTONE_PERMISSIONS.edit);
export const canDeleteMilestones = (user) => can(user, MILESTONE_PERMISSIONS.delete);

/**
 * Can the viewer read organization milestone achievements/history?
 * View Milestones or any management grant (Create/Edit/Delete permit reading
 * the definitions needed for the management operation).
 */
export const canViewOrganizationHistory = (user) => canViewMilestoneManagement(user);
